import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TracerService } from './tracer.service';
import { Message, Request, Session, SessionResponse, Trace } from '../models/tracer.model';

// Public types ------------------------------------------------------------

/** One leaf-level difference between A and B at a specific JSON path. */
export interface DiffLeaf {
  /** Dot-bracket notation, e.g. "originDestinations[0].departure.date". */
  path: string;
  kind: 'changed' | 'added' | 'removed';
  leftValue?: unknown;
  rightValue?: unknown;
}

/** A leaf whose path/key/value matches the exclusion ruleset and is NOT
 *  reported as a difference. Surfaced separately so operators can see
 *  how many were filtered (the "47 excluded as dynamic" counter). */
export interface ExcludedLeaf {
  path: string;
  reason:
    | 'identifier-key'
    | 'identifier-value'
    | 'timestamp-key'
    | 'timestamp-value'
    | 'uuid-value';
}

/** Per-payload diff (one Message-name slot per request, e.g. "input"). */
export interface PayloadDiff {
  /** "input" / "output" / "provider_request" / "provider_response" / ... */
  messageName: string;
  /** "json-api" / "sidecar" / "requester" / "unknown" */
  source: string;
  /** Provider list for requester traces, undefined for inbound. */
  providers?: string;
  /** "json" / "xml" / "raw" / etc. */
  format: string;
  /** Real differences after exclusion. */
  diffs: DiffLeaf[];
  /** Differences filtered out by the dynamic-field ruleset. */
  excluded: ExcludedLeaf[];
  /** Set when either side's body wasn't parseable as JSON. */
  unparseable?: boolean;
  /** Set when one side has the message but the other doesn't. */
  onlyOn?: 'a' | 'b';
  /** Set when the format isn't JSON — we don't diff XML/raw bodies. */
  skippedNonJson?: boolean;
  /** Set when one side's body was too big to fetch inline. */
  skippedTooLarge?: boolean;
  /** Free-form note used by the UI (size info, error, etc.). */
  note?: string;
}

export interface RequestPair {
  status: 'paired' | 'only_a' | 'only_b';
  a?: Request;
  b?: Request;
  /** Populated only when status === 'paired'. */
  payloadDiffs?: PayloadDiff[];
  /** Convenience totals, populated after payload diffs run. */
  totalDiffs?: number;
  totalExcluded?: number;
}

export interface SessionDiff {
  sessionA: Session;
  sessionB: Session;
  pairs: RequestPair[];
  totalDiffs: number;
  totalExcluded: number;
  pairedWithDiffs: number;
  pairedIdentical: number;
  unpaired: number;
  computedAtMs: number;
}

// Exclusion ruleset (mirrors the spec called out in the mockup) -----------
//
// Three categories:
//   1. Keys we never want to compare (their VALUE varies by definition
//      per session/request even when the semantic content is the same).
//   2. Value patterns that look like identifiers we know are dynamic.
//   3. Value patterns that look like timestamps in known time-bearing
//      keys (ISO-8601 / Unix epoch).
//
// Match is case-insensitive on key names. The ruleset is exported so the
// /diff page can show "view excluded fields" detail.

/** Keys whose value is by definition session-/request-local. Case-insensitive
 *  match against the LAST path segment. */
export const IDENTIFIER_KEYS = new Set<string>([
  'sessionid',
  'session_id',
  'ag-session-id',
  'requestid',
  'request_id',
  'ag-request-id',
  'x-request-id',
  'replay_request_id',
  'replayrequestid',
  'offerid',
  'offerids',
  'shoppingresponseid',
  'orderid',
  // Note: top-level "id" is NOT in this list — it's ambiguous (could be a
  // legitimate enum value). We catch the dynamic cases via the value
  // regexes below instead.
]);

/** Keys whose value is by definition a per-write timestamp. Excluded
 *  outright. */
export const TIMESTAMP_KEYS = new Set<string>([
  'createdat',
  'created_at',
  'updatedat',
  'updated_at',
  'bookingdate',
  'paymenttimelimit',
  'priceguaranteetimelimit',
  'offerexpiration',
  'expirationtime',
  'timestamp',
  'date_time',
  'datetime',
  'lastmodified',
  'last_modified',
]);

/** A value that LOOKS like an identifier we know varies (UUID, sess-/req-
 *  prefix, AGW offer id base64-ish encoded ref). */
const IDENTIFIER_VALUE_RE =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?:sess|req)-[A-Za-z0-9\-]+)$/i;

/** ISO-8601 datetime — broad enough to catch "2026-05-08T14:32:09Z",
 *  "2026-05-08T14:32:09.123456Z", "2026-05-08 14:32:09+02:00". */
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/;

// Diff engine -------------------------------------------------------------

/**
 * Walk left and right in parallel, emit leaf-level differences with
 * dot-bracket paths. Apply the exclusion ruleset and split into
 * { diffs, excluded } so the UI can show both bands.
 */
export function deepDiff(
  left: unknown,
  right: unknown
): { diffs: DiffLeaf[]; excluded: ExcludedLeaf[] } {
  const diffs: DiffLeaf[] = [];
  const excluded: ExcludedLeaf[] = [];
  walk(left, right, [], diffs, excluded);
  return { diffs, excluded };
}

function walk(
  left: unknown,
  right: unknown,
  segments: (string | number)[],
  diffs: DiffLeaf[],
  excluded: ExcludedLeaf[]
): void {
  // Same reference / both undefined → no diff.
  if (left === right) return;

  const lType = typeOf(left);
  const rType = typeOf(right);

  // Object × Object → walk keys (union).
  if (lType === 'object' && rType === 'object') {
    const lObj = left as Record<string, unknown>;
    const rObj = right as Record<string, unknown>;
    const keys = new Set([...Object.keys(lObj), ...Object.keys(rObj)]);
    for (const k of keys) {
      walk(lObj[k], rObj[k], [...segments, k], diffs, excluded);
    }
    return;
  }

  // Array × Array → walk by index, longer side's tail = added/removed.
  if (lType === 'array' && rType === 'array') {
    const lArr = left as unknown[];
    const rArr = right as unknown[];
    const max = Math.max(lArr.length, rArr.length);
    for (let i = 0; i < max; i++) {
      walk(lArr[i], rArr[i], [...segments, i], diffs, excluded);
    }
    return;
  }

  // Type mismatch or scalar inequality → leaf.
  emitLeaf(left, right, segments, diffs, excluded);
}

function emitLeaf(
  left: unknown,
  right: unknown,
  segments: (string | number)[],
  diffs: DiffLeaf[],
  excluded: ExcludedLeaf[]
): void {
  const path = formatPath(segments);
  const kind: DiffLeaf['kind'] =
    left === undefined ? 'added' : right === undefined ? 'removed' : 'changed';

  // Exclusion: last key segment.
  const lastKey = lastKeySegment(segments);
  if (lastKey) {
    if (IDENTIFIER_KEYS.has(lastKey)) {
      excluded.push({ path, reason: 'identifier-key' });
      return;
    }
    if (TIMESTAMP_KEYS.has(lastKey)) {
      excluded.push({ path, reason: 'timestamp-key' });
      return;
    }
  }

  // Exclusion: BOTH sides look like an identifier or timestamp value.
  // We exclude only when BOTH match the pattern — a left=UUID,
  // right="foo" comparison IS a meaningful diff (the field changed
  // shape) and should be reported.
  if (looksLikeIdentifierValue(left) && looksLikeIdentifierValue(right)) {
    excluded.push({ path, reason: 'identifier-value' });
    return;
  }
  if (looksLikeUUIDValue(left) && looksLikeUUIDValue(right)) {
    excluded.push({ path, reason: 'uuid-value' });
    return;
  }
  if (looksLikeTimestampValue(left) && looksLikeTimestampValue(right)) {
    excluded.push({ path, reason: 'timestamp-value' });
    return;
  }

  diffs.push({ path, kind, leftValue: left, rightValue: right });
}

function typeOf(v: unknown): 'object' | 'array' | 'scalar' | 'undefined' {
  if (v === undefined) return 'undefined';
  if (v === null) return 'scalar';
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'object') return 'object';
  return 'scalar';
}

function lastKeySegment(segments: (string | number)[]): string | null {
  for (let i = segments.length - 1; i >= 0; i--) {
    if (typeof segments[i] === 'string') {
      return (segments[i] as string).toLowerCase();
    }
  }
  return null;
}

/** Dot-bracket notation: foo[0].bar.baz */
export function formatPath(segments: (string | number)[]): string {
  let out = '';
  for (const seg of segments) {
    if (typeof seg === 'number') {
      out += `[${seg}]`;
    } else {
      out += out === '' ? seg : `.${seg}`;
    }
  }
  return out;
}

function looksLikeIdentifierValue(v: unknown): boolean {
  return typeof v === 'string' && IDENTIFIER_VALUE_RE.test(v);
}

function looksLikeUUIDValue(v: unknown): boolean {
  // The IDENTIFIER_VALUE_RE already covers UUIDs; keeping this hook so
  // a future stricter UUID-only path can be plugged in without touching
  // callers.
  return looksLikeIdentifierValue(v);
}

function looksLikeTimestampValue(v: unknown): boolean {
  if (typeof v === 'string') {
    return ISO_DATETIME_RE.test(v);
  }
  // Unix epoch numbers in known time keys are caught by the KEY check
  // above; we don't try to guess numeric timestamps without context.
  return false;
}

// Pairing -----------------------------------------------------------------

/**
 * Pair requests across two sessions by (Method, position-within-method).
 * Preserves A's overall sequence — unpaired-A rows render in their
 * original position; unpaired-B rows append at the end.
 *
 * The "position-within-method" heuristic handles the common case where
 * both sessions ran the same flow once (AS → OP → OC → OR). For
 * sessions with multiple same-method requests it pairs in original
 * order, which the operator can re-pair manually (UI affordance is
 * shipped as a placeholder; full drag-to-pair is a follow-up).
 */
export function pairRequests(reqsA: Request[], reqsB: Request[]): RequestPair[] {
  // Index B by Method, keep a "consumed" flag so we don't reuse.
  const bByMethod = new Map<string, { req: Request; taken: boolean }[]>();
  for (const r of reqsB) {
    const m = normaliseMethod(r.method);
    const list = bByMethod.get(m) ?? [];
    list.push({ req: r, taken: false });
    bByMethod.set(m, list);
  }

  const pairs: RequestPair[] = [];
  for (const a of reqsA) {
    const m = normaliseMethod(a.method);
    const list = bByMethod.get(m);
    const slot = list?.find(x => !x.taken);
    if (slot) {
      slot.taken = true;
      pairs.push({ status: 'paired', a, b: slot.req });
    } else {
      pairs.push({ status: 'only_a', a });
    }
  }
  // Anything left in B is appended as only_b at the end.
  for (const list of bByMethod.values()) {
    for (const slot of list) {
      if (!slot.taken) {
        pairs.push({ status: 'only_b', b: slot.req });
      }
    }
  }
  return pairs;
}

function normaliseMethod(m: string | undefined): string {
  return (m ?? '').trim().toLowerCase();
}

// Orchestrator ------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class SessionDiffService {
  constructor(private readonly tracer: TracerService) {}

  /**
   * Top-level entrypoint. Fetches both sessions, pairs requests,
   * fetches message bodies for every paired request, runs deepDiff
   * over each (name-matched) payload pair, and rolls up the totals.
   *
   * Phase 1 — client-side. Heavy on round-trips for big sessions; a
   * Phase 2 server-side endpoint would consolidate this into one HTTP
   * call. See the diff-tool mockup index for the staged plan.
   */
  async diffSessions(sessionAValue: string, sessionBValue: string): Promise<SessionDiff> {
    const startedAt = Date.now();
    const [respA, respB] = await Promise.all([
      firstValueFrom(this.tracer.getSessionData(sessionAValue)),
      firstValueFrom(this.tracer.getSessionData(sessionBValue)),
    ]);
    assertSessionResponse(respA, sessionAValue);
    assertSessionResponse(respB, sessionBValue);

    const pairs = pairRequests(respA.requests || [], respB.requests || []);

    // Diff each paired request's payloads in parallel.
    await Promise.all(pairs.filter(p => p.status === 'paired').map(p => this.diffPair(p)));

    let totalDiffs = 0;
    let totalExcluded = 0;
    let pairedWithDiffs = 0;
    let pairedIdentical = 0;
    let unpaired = 0;
    for (const p of pairs) {
      if (p.status === 'paired') {
        if ((p.totalDiffs ?? 0) > 0) pairedWithDiffs++;
        else pairedIdentical++;
        totalDiffs += p.totalDiffs ?? 0;
        totalExcluded += p.totalExcluded ?? 0;
      } else {
        unpaired++;
      }
    }

    return {
      sessionA: respA.session,
      sessionB: respB.session,
      pairs,
      totalDiffs,
      totalExcluded,
      pairedWithDiffs,
      pairedIdentical,
      unpaired,
      computedAtMs: Date.now() - startedAt,
    };
  }

  /**
   * Diff every name-matched message in this paired pair. Message-name
   * pairing (input ↔ input, output ↔ output, …) is the simplest stable
   * pairing rule — Phase 2 will refine for multi-trace flows.
   */
  private async diffPair(pair: RequestPair): Promise<void> {
    if (!pair.a || !pair.b) return;
    const messagesA = collectMessages(pair.a);
    const messagesB = collectMessages(pair.b);

    // Pair by message Name (input ↔ input, output ↔ output, etc.).
    const byNameA = new Map<string, Message>();
    const byNameB = new Map<string, Message>();
    for (const m of messagesA) {
      if (!byNameA.has(m.name)) byNameA.set(m.name, m);
    }
    for (const m of messagesB) {
      if (!byNameB.has(m.name)) byNameB.set(m.name, m);
    }
    const names = new Set([...byNameA.keys(), ...byNameB.keys()]);

    const payloadDiffs: PayloadDiff[] = [];
    for (const name of names) {
      const ma = byNameA.get(name);
      const mb = byNameB.get(name);
      payloadDiffs.push(await this.diffMessages(name, ma, mb));
    }
    pair.payloadDiffs = payloadDiffs;
    pair.totalDiffs = payloadDiffs.reduce((n, p) => n + p.diffs.length, 0);
    pair.totalExcluded = payloadDiffs.reduce((n, p) => n + p.excluded.length, 0);
  }

  /**
   * Pull both message bodies, parse as JSON, run deepDiff. Various
   * skip-with-note paths for missing-side / non-JSON / too-large.
   */
  private async diffMessages(name: string, a?: Message, b?: Message): Promise<PayloadDiff> {
    const base: PayloadDiff = {
      messageName: name,
      source: 'unknown',
      format: a?.format ?? b?.format ?? '',
      diffs: [],
      excluded: [],
    };
    if (!a || !b) {
      return { ...base, onlyOn: a ? 'a' : 'b' };
    }
    const format = (a.format || b.format || '').toLowerCase();
    if (format && format !== 'json') {
      return { ...base, format, skippedNonJson: true, note: `format=${format}` };
    }

    try {
      const [bodyA, bodyB] = await Promise.all([this.fetchBody(a.id), this.fetchBody(b.id)]);
      if (bodyA === null || bodyB === null) {
        return { ...base, format, skippedTooLarge: true, note: 'body too large to inline' };
      }
      let parsedA: unknown;
      let parsedB: unknown;
      try {
        parsedA = bodyA === '' ? undefined : JSON.parse(bodyA);
        parsedB = bodyB === '' ? undefined : JSON.parse(bodyB);
      } catch (e) {
        return {
          ...base,
          format,
          unparseable: true,
          note: e instanceof Error ? e.message : 'JSON parse error',
        };
      }
      const { diffs, excluded } = deepDiff(parsedA, parsedB);
      return { ...base, format, diffs, excluded };
    } catch (e) {
      return {
        ...base,
        format,
        note: e instanceof Error ? e.message : 'fetch error',
      };
    }
  }

  /**
   * Fetch a message body via the existing /message/:id endpoint.
   * Returns null when the body is in Tier 2/3 (presigned-only or
   * zip-only) — Phase 2 will follow the presigned URL; for now we
   * skip with a note.
   */
  private async fetchBody(messageID: number): Promise<string | null> {
    const resp = await firstValueFrom(this.tracer.getMessage(messageID));
    if (resp.zip_only || resp.display_on_demand) return null;
    return resp.body ?? '';
  }
}

// Helpers -----------------------------------------------------------------

function assertSessionResponse(resp: SessionResponse, sessionValue: string): void {
  if (resp.error) {
    throw new Error(`Session ${sessionValue}: ${resp.error}`);
  }
  if (!resp.session) {
    throw new Error(`Session ${sessionValue}: empty session in response`);
  }
}

/** Flatten Request.traces[].messages into one array. The diff orchestrator
 *  pairs them by Name — multi-trace per request is a Phase 2 refinement. */
function collectMessages(r: Request): Message[] {
  const out: Message[] = [];
  for (const t of r.traces || []) {
    for (const m of t.messages || []) {
      out.push(m);
    }
  }
  return out;
}
